import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logo from "./assets/logo.png";
import NotificationSnackbar from "./NotificationBar";

const SurveyFormView = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [groupedQuestionsData, setGroupedQuestionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveyInfo, setSurveyInfo] = useState({});
  const [responses, setResponses] = useState({});
  const [otherResponses, setOtherResponses] = useState({});
  const [visibleQuestions, setVisibleQuestions] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [apiResponses, setApiResponses] = useState({});
  const QUESTIONS_PER_PAGE = 2;
  const questionsContainerRef = useRef(null);
  const fetchedUrls = useRef(new Set());
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const BACKGROUND_COLOR = "linear-gradient(195deg, #0a2e52 0%, #1565C0 100%)";

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (e) {}
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const calculateVisibleQuestions = (groupedQuestionsArray, responses) => {
    const visibleSet = new Set();
    const questionMap = {};

    groupedQuestionsArray.forEach((q) => {
      questionMap[q.questionId] = q;
    });
    const addQuestionAndSuccessors = (questionId, visited = new Set()) => {
      if (!questionId || visited.has(questionId)) return;
      visited.add(questionId);
      const question = questionMap[questionId];
      if (!question) return;
      visibleSet.add(questionId);
      if (question.questionType === "MULTIPLE CHOICE") {
        const userResponse = responses[questionId];
        if (!userResponse) return;

        const selectedOption = question.options?.find(
          (opt) => opt.optionText === userResponse,
        );

        if (selectedOption?.enableSuccessor && question.successor) {
          addQuestionAndSuccessors(question.successor, visited);
        }
      } else {
        if (question.successor) {
          addQuestionAndSuccessors(question.successor, visited);
        }
      }
    };

    const topLevelQuestions = groupedQuestionsArray.filter(
      (q) =>
        !groupedQuestionsArray.some(
          (parent) => parent.successor === q.questionId,
        ),
    );
    topLevelQuestions.forEach((q) => addQuestionAndSuccessors(q.questionId));
    return visibleSet;
  };
  
  const fetchQuestions = async () => {
    try {
      const res = await fetch(`/api/support/survey-questions/${surveyId}`);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format from server");
      }
      let questionsArray;
      if (Array.isArray(data)) {
        questionsArray = data;
      } else if (typeof data === "object" && data.questions) {
        questionsArray = Array.isArray(data.questions) ? data.questions : [];
      } else {
        const isGroupedObject = Object.values(data).every(
          (item) => item.questionId && item.questionText,
        );

        if (isGroupedObject) {
          const groupedArray = Object.values(data);
          setQuestions([]);
          setSurveyInfo({
            title: groupedArray[0]?.SurveyTitle || "Survey",
            description: groupedArray[0]?.SurveyDescription || "",
          });

          const initialResponses = {};
          groupedArray.forEach((q) => {
            initialResponses[q.questionId] = "";
          });
          setResponses(initialResponses);
          const initialVisible = calculateVisibleQuestions(
            groupedArray,
            initialResponses,
          );
          setVisibleQuestions(initialVisible);
          setGroupedQuestionsData(groupedArray);
          setLoading(false);
          return;
        } else {
          questionsArray = Object.values(data);
        }
      }

      setQuestions(questionsArray);
      const grouped = groupQuestions(questionsArray);
      const groupedArray = Object.values(grouped);
      if (groupedArray.length > 0) {
        setSurveyInfo({
          title: groupedArray[0].SurveyTitle || "Survey",
          description: groupedArray[0].SurveyDescription || "",
        });
      }
      const initialResponses = {};
      groupedArray.forEach((q) => {
        initialResponses[q.questionId] = "";
      });

      setResponses(initialResponses);
      const initialVisible = calculateVisibleQuestions(
        groupedArray,
        initialResponses,
      );
      setVisibleQuestions(initialVisible);
      setGroupedQuestionsData(groupedArray);
    } catch (error) {
      console.error("Error fetching survey questions:", error);
      handleSnackbarOpen("Failed to load survey. Please try again.", "error");
      setSurveyInfo({
        title: "Survey Not Found",
        description:
          "Unable to load survey questions. Please check the survey ID or try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (surveyId) {
      fetchQuestions();
    }
  }, [surveyId]);

  useEffect(() => {
    if (groupedQuestionsData.length > 0) {
      const newVisibleQuestions = calculateVisibleQuestions(
        groupedQuestionsData,
        responses,
      );
      setVisibleQuestions(newVisibleQuestions);
    }
  }, [responses, groupedQuestionsData]);

  useEffect(() => {
    const fetchApis = () => {
      const apisToFetch = [
        ...new Set(
          groupedQuestionsData
            .filter((q) => q.hasApi && q.api)
            .map((q) => q.api),
        ),
      ];

      apisToFetch.forEach((apiUrl) => {
        if (fetchedUrls.current.has(apiUrl)) return;
        fetchedUrls.current.add(apiUrl);
        setApiResponses((prev) => ({ ...prev, [apiUrl]: "LOADING" }));

        fetch(apiUrl)
          .then((res) => {
            if (!res.ok) throw new Error("API response not ok");
            return res.json();
          })
          .then((data) => {
            setApiResponses((prev) => ({ ...prev, [apiUrl]: data }));
          })
          .catch((err) => {
            console.error("Failed to fetch API endpoint:", apiUrl, err);
            setApiResponses((prev) => ({ ...prev, [apiUrl]: null }));
          });
      });
    };

    if (groupedQuestionsData.length > 0) {
      fetchApis();
    }
  }, [groupedQuestionsData]);

  const groupQuestions = (questions) => {
    if (!questions) return {};
    if (Array.isArray(questions) && questions.length === 0) return {};
    if (typeof questions !== "object") return {};
    if (!Array.isArray(questions)) {
      const firstValue = Object.values(questions)[0];
      if (firstValue && firstValue.questionId) {
        return questions;
      }
    }

    try {
      return questions.reduce((acc, question) => {
        if (!question || !question.QuestionId) return acc;

        const questionId = question.QuestionId;

        if (!acc[questionId]) {
          acc[questionId] = {
            questionId: questionId,
            questionText: question.questionText || "",
            questionType: (question.questionType || "").toUpperCase(),
            isRequired: Boolean(question.isRequired),
            canPickDate: Boolean(question.canPickDate),
            hasApi: Boolean(question.hasApi),
            api: question.api || null,
            isNumber: Boolean(question.isNumber),
            successor: question.successor,
            SurveyTitle: question.SurveyTitle || "",
            SurveyDescription: question.SurveyDescription || "",
            options: [],
          };
        }

        if (question.OptionId) {
          acc[questionId].options.push({
            optionId: question.OptionId,
            optionText: question.optionText || "",
            displayOrder: question.displayOrder || 0,
            enableSuccessor: Boolean(question.enableSuccessor),
            allowtyping: Boolean(question.allowtyping),
            isNbr: Boolean(question.isNbr),
            hasAnotherQuestion: Boolean(question.hasAnotherQuestion),
            questionTexts: question.questionTexts || "",
            relatedQuestion: question.relatedQuestion || null,
          });
        }
        return acc;
      }, {});
    } catch (error) {
      console.error("Error grouping questions:", error);
      return {};
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleOtherResponseChange = (questionId, value) => {
    setOtherResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const sortedQuestions = groupedQuestionsData.sort(
    (a, b) => a.questionId - b.questionId,
  );
  const visibleSortedQuestions = sortedQuestions.filter((q) =>
    visibleQuestions.has(q.questionId),
  );

  const totalPages = Math.max(
    1,
    Math.ceil(visibleSortedQuestions.length / QUESTIONS_PER_PAGE),
  );

  const validateCurrentPage = () => {
    if (!isEmailValidated) return false;
    const startIdx = (currentPage - 1) * QUESTIONS_PER_PAGE;
    const endIdx = currentPage * QUESTIONS_PER_PAGE;
    const currentQuestions = sortedQuestions.slice(startIdx, endIdx);

    for (const question of currentQuestions) {
      if (visibleQuestions.has(question.questionId) && question.isRequired) {
        if (
          !responses[question.questionId] ||
          responses[question.questionId].toString().trim() === ""
        ) {
          handleSnackbarOpen(
            `Please answer the required question: ${question.questionText}`,
            "error",
          );
          return false;
        }
        if (question.questionType === "MULTIPLE CHOICE") {
          const selectedOption = question.options.find(
            (opt) => opt.optionText === responses[question.questionId],
          );
          if (selectedOption && selectedOption.allowtyping) {
            if (
              !otherResponses[question.questionId] ||
              otherResponses[question.questionId].toString().trim() === ""
            ) {
              handleSnackbarOpen(
                `Please specify your answer for: ${question.questionText}`,
                "error",
              );
              return false;
            }
          }
          if (selectedOption && selectedOption.hasAnotherQuestion) {
            if (
              !responses[selectedOption.relatedQuestion] ||
              responses[selectedOption.relatedQuestion].toString().trim() === ""
            ) {
              handleSnackbarOpen(
                `Please answer: ${selectedOption.questionTexts}`,
                "error",
              );
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  const handleNextPage = () => {
    if (validateCurrentPage()) {
      setCurrentPage((p) => Math.min(p + 1, totalPages));
    }
  };

  useEffect(() => {
    if (questionsContainerRef.current) {
      questionsContainerRef.current.scrollTop = 0;
    }
    try {
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (e) {}
  }, [currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleSubmit = async () => {
    if (!isEmailValidated) {
      handleSnackbarOpen("Please Enter your email first.", "error");
      return;
    }
    try {
      for (const question of sortedQuestions) {
        if (visibleQuestions.has(question.questionId) && question.isRequired) {
          if (
            !responses[question.questionId] ||
            responses[question.questionId].toString().trim() === ""
          ) {
            handleSnackbarOpen(`Please answer all required questions`, "error");
            return;
          }

          if (question.questionType === "MULTIPLE CHOICE") {
            const selectedOption = question.options.find(
              (opt) => opt.optionText === responses[question.questionId],
            );
            if (selectedOption && selectedOption.allowtyping) {
              if (
                !otherResponses[question.questionId] ||
                otherResponses[question.questionId].toString().trim() === ""
              ) {
                handleSnackbarOpen(
                  `Please specify your answers for all required questions`,
                  "error",
                );
                return;
              }
            }
            if (selectedOption && selectedOption.hasAnotherQuestion) {
              if (
                !responses[selectedOption.relatedQuestion] ||
                responses[selectedOption.relatedQuestion].toString().trim() === ""
              ) {
                handleSnackbarOpen(
                  `Please answer: ${selectedOption.questionTexts}`,
                  "error",
                );
                return;
              }
            }
          }
        }
      }
      if (!feedback.trim()) {
        handleSnackbarOpen(
          "Please provide feedback to submit the survey.",
          "error",
        );
        return;
      }

      setSubmitting(true);

      // Using Map to automatically handle any ID duplication caused by related questions.
      const visibleResponsesMap = new Map();

      Object.entries(responses).forEach(([questionId, response]) => {
        const qIdInt = parseInt(questionId);
        if (visibleQuestions.has(qIdInt)) {
          let finalResponse = response.toString();
          const q = sortedQuestions.find((sq) => sq.questionId === qIdInt);
          
          if (q && q.questionType === "MULTIPLE CHOICE") {
            const selectedOption = q.options.find(
              (opt) => opt.optionText === response,
            );
            if (selectedOption) {
              if (selectedOption.allowtyping) {
                const extraText = otherResponses[questionId] || "";
                finalResponse = extraText
                  ? `${response} - ${extraText}`
                  : response;
              }
              // Extract data for secondary related question without marking the whole object visible 
              if (
                selectedOption.hasAnotherQuestion &&
                selectedOption.relatedQuestion
              ) {
                const relatedAns = responses[selectedOption.relatedQuestion];
                if (relatedAns) {
                  visibleResponsesMap.set(
                    selectedOption.relatedQuestion,
                    relatedAns.toString()
                  );
                }
              }
            }
          }
          
          visibleResponsesMap.set(qIdInt, finalResponse);
        }
      });

      const visibleResponses = Array.from(visibleResponsesMap, ([questionId, response]) => ({
        questionId,
        response,
      }));

      const submissionData = {
        surveyId,
        email,
        userId: userDetails?.id,
        comment: feedback.trim(),
        responses: visibleResponses,
      };

      const response = await fetch("/api/support/survey-responses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData),
      });
      if (!response.ok) throw new Error("Failed to submit survey responses");
      handleSnackbarOpen("Survey submitted successfully!", "success");
      setTimeout(() => {
        setResponses({});
        setOtherResponses({});
        setFeedback("");
        setEmail("");
        setIsEmailValidated(false);
        setUserDetails(null);
        setCurrentPage(1);
        setVisibleQuestions(new Set());
        window.location.replace("/");
      }, 1200);
    } catch (error) {
      console.error("Error submitting survey:", error);
      handleSnackbarOpen("Failed to submit survey. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSurvey = () => {
    navigate("/");
  };

  const handleEmailCheck = async () => {
    if (!email.trim()) {
      handleSnackbarOpen("Please enter an email.", "error");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      handleSnackbarOpen("Please enter a valid email address.", "error");
      return;
    }
    setEmailLoading(true);
    try {
      const res = await fetch("/api/support/verify-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          surveyId: surveyId,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        handleSnackbarOpen(data, "error");
        return;
      }

      setUserDetails(data);
      setIsEmailValidated(true);
      handleSnackbarOpen(
        `Welcome ${data.firstName} ${data.lastName} to this survey!!`,
        "success",
      );
    } catch (error) {
      console.error("Error verifying email:", error);
      handleSnackbarOpen(
        "Failed to validate email. Please try again.",
        "error",
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handleEmailCheck();
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: BACKGROUND_COLOR,
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  if (!loading && groupedQuestionsData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: BACKGROUND_COLOR,
          color: "white",
          textAlign: "center",
        }}
      >
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" color="error">
            No questions found for this survey.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const paginatedQuestions = visibleSortedQuestions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE,
  );
  const isLastPage = currentPage === totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        width: "100%",
        background: BACKGROUND_COLOR,
        padding: { xs: "6px", sm: "5px" },
        boxSizing: "border-box",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "800px",
          mt: { xs: 0, sm: "60px" },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            padding: { xs: "20px 16px", sm: "32px" },
            borderRadius: "16px",
            backgroundColor: "#fff",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #3b82f6, #1e40af)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={logo}
                alt="Logo"
                style={{
                  width: "48px",
                  height: "48px",
                  marginRight: "12px",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #1e40af, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.5px",
                }}
              >
                Survey Form
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseSurvey}
              aria-label="close"
              sx={{
                color: "#64748b",
                "&:hover": {
                  backgroundColor: "rgba(100, 116, 139, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              marginBottom: "24px",
              padding: "24px",
              borderLeft: `4px solid ${PRIMARY_COLOR}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                marginBottom: "12px",
                color: PRIMARY_COLOR,
              }}
            >
              {surveyInfo.title || "Survey Title"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#5f6368",
                lineHeight: "1.6",
                fontSize: "1rem",
              }}
            >
              {surveyInfo.description || "Survey description will appear here."}
            </Typography>
            <Box sx={{ mt: 3 }}>
              {!isEmailValidated ? (
                <>
                  <TextField
                    fullWidth
                    label="Enter your IPPIS registered email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleEmailInputKeyPress}
                    disabled={emailLoading}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 8,
                      },
                    }}
                    helperText="Please enter the email address registered in the IPPIS system"
                  />
                  <Button
                    variant="contained"
                    onClick={handleEmailCheck}
                    disabled={emailLoading || !email.trim()}
                    sx={{
                      mt: 1,
                      py: 1.5,
                      px: 3,
                      borderRadius: 8,
                      backgroundColor:
                        emailLoading || !email.trim()
                          ? "#cccccc"
                          : PRIMARY_COLOR,
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          emailLoading || !email.trim()
                            ? "#cccccc"
                            : SECONDARY_COLOR,
                      },
                    }}
                  >
                    {emailLoading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Enter your Email"
                    )}
                  </Button>
                </>
              ) : currentPage === 1 ? (
                <Box
                  sx={{
                    backgroundColor: "#f0f9ff",
                    borderRadius: "8px",
                    padding: "16px",
                    border: "1px solid #0ea5e9",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#0c4a6e",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    ✓ Personal Information
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#075985",
                    }}
                  >
                    Welcome, {userDetails?.firstName} {userDetails?.lastName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#075985",
                    }}
                  >
                    UserId: {userDetails?.id}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#075985",
                    }}
                  >
                    Email: {email}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
          {isEmailValidated && (
            <>
              <Box
                sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}
                ref={questionsContainerRef}
              >
                {paginatedQuestions.map((question) => (
                  <Box
                    key={question.questionId}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      marginBottom: "24px",
                      padding: "24px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: "16px",
                        color: PRIMARY_COLOR,
                        fontSize: "1.1rem",
                      }}
                    >
                      {question.questionText}
                      {question.isRequired && (
                        <span style={{ color: "#d93025", marginLeft: "4px" }}>
                          *
                        </span>
                      )}
                    </Typography>

                    <Box sx={{ marginLeft: "8px" }}>
                      {/* --- OPEN QUESTION HANDLER --- */}
                      {question.questionType === "OPEN QUESTION" && (
                        <Box sx={{ mt: 1, mr: 2, mb: 2 }}>
                          {question.hasApi ? (
                            (() => {
                              const apiData = apiResponses[question.api];
                              if (apiData === "LOADING") {
                                return <CircularProgress size={24} />;
                              }
                              if (!apiData) {
                                return (
                                  <Typography color="error" variant="body2">
                                    Failed to load options from API.
                                  </Typography>
                                );
                              }

                              let options = [];
                              const qText = question.questionText.toLowerCase();
                              let placeholderText = "Select Option";
                              let isDropdownDisabled = false;
                              let selectedDistrict = null;

                              if (qText.includes("district")) {
                                placeholderText = "Select District";
                                if (Array.isArray(apiData)) {
                                  options = apiData.map((d) => d.name || d);
                                } else if (apiData.districts) {
                                  options = apiData.districts.map(
                                    (d) => d.name,
                                  );
                                }
                              } else if (qText.includes("sector")) {
                                const districtQuestion = [...sortedQuestions]
                                  .filter(
                                    (q) =>
                                      q.questionId < question.questionId &&
                                      q.questionText
                                        .toLowerCase()
                                        .includes("district") &&
                                      q.api === question.api,
                                  )
                                  .pop();

                                selectedDistrict = districtQuestion
                                  ? responses[districtQuestion.questionId]
                                  : null;

                                if (!selectedDistrict) {
                                  placeholderText =
                                    "Please select a District first";
                                  isDropdownDisabled = true;
                                } else {
                                  placeholderText = "Select Sector";
                                  if (apiData.districts) {
                                    const distObj = apiData.districts.find(
                                      (d) => d.name === selectedDistrict,
                                    );
                                    if (distObj && distObj.sectors) {
                                      options = distObj.sectors.map(
                                        (s) => s.name,
                                      );
                                    }
                                  }
                                }
                              }

                              return (
                                <Autocomplete
                                  options={options}
                                  disabled={isDropdownDisabled}
                                  value={responses[question.questionId] || null}
                                  onChange={(event, newValue) => {
                                    const val = newValue || "";
                                    handleResponseChange(
                                      question.questionId,
                                      val,
                                    );
                                    if (qText.includes("district")) {
                                      const sectorQuestion =
                                        sortedQuestions.find(
                                          (q) =>
                                            q.questionId >
                                              question.questionId &&
                                            q.questionText
                                              .toLowerCase()
                                              .includes("sector") &&
                                            q.api === question.api,
                                        );
                                      if (sectorQuestion) {
                                        handleResponseChange(
                                          sectorQuestion.questionId,
                                          "",
                                        );
                                      }
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={placeholderText}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "10px",
                                          backgroundColor: isDropdownDisabled
                                            ? "#f1f5f9"
                                            : "#f8fafc",
                                          transition: "all 0.2s ease-in-out",
                                          "&:hover fieldset": {
                                            borderColor: isDropdownDisabled
                                              ? "#e2e8f0"
                                              : PRIMARY_COLOR,
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: PRIMARY_COLOR,
                                            borderWidth: "2px",
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    sx: {
                                      maxHeight: 280,
                                      "&::-webkit-scrollbar": {
                                        width: "6px",
                                      },
                                      "&::-webkit-scrollbar-track": {
                                        background: "#f1f1f1",
                                        borderRadius: "8px",
                                      },
                                      "&::-webkit-scrollbar-thumb": {
                                        background: "#cbd5e1",
                                        borderRadius: "8px",
                                        "&:hover": {
                                          background: "#94a3b8",
                                        },
                                      },
                                    },
                                  }}
                                  PaperComponent={(props) => (
                                    <Paper
                                      {...props}
                                      sx={{
                                        borderRadius: "12px",
                                        boxShadow:
                                          "0px 10px 30px rgba(0, 0, 0, 0.12)",
                                        border: "1px solid #e2e8f0",
                                        mt: 1,
                                      }}
                                    />
                                  )}
                                />
                              );
                            })()
                          ) : question.canPickDate ? (
                            <TextField
                              type="date"
                              fullWidth
                              value={responses[question.questionId] || ""}
                              onChange={(e) =>
                                handleResponseChange(
                                  question.questionId,
                                  e.target.value,
                                )
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                },
                              }}
                            />
                          ) : (
                            <TextField
                              fullWidth
                              type={question.isNumber ? "number" : "text"}
                              multiline={!question.isNumber}
                              rows={question.isNumber ? 1 : 3}
                              placeholder={
                                question.isNumber
                                  ? "Enter a number..."
                                  : "Type your answer here..."
                              }
                              value={responses[question.questionId] || ""}
                              onChange={(e) => {
                                let val = e.target.value;
                                if (question.isNumber) {
                                  val = val.replace(/[^0-9.]/g, "");
                                }
                                handleResponseChange(question.questionId, val);
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                },
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {question.questionType === "MULTIPLE CHOICE" &&
                        question.options.map((option) => {
                          const relatedQ = option.hasAnotherQuestion
                            ? sortedQuestions.find(q => q.questionId === option.relatedQuestion)
                            : null;
                          const isRelatedPickDate = relatedQ ? relatedQ.canPickDate : false;

                          return (
                            <Box
                              key={option.optionId}
                              sx={{ marginBottom: "16px" }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "#f8fafc",
                                  },
                                  borderRadius: "4px",
                                  padding: "8px",
                                }}
                                onClick={() =>
                                  handleResponseChange(
                                    question.questionId,
                                    option.optionText,
                                  )
                                }
                              >
                                <input
                                  type="radio"
                                  name={`question_${question.questionId}`}
                                  value={option.optionText}
                                  checked={
                                    responses[question.questionId] ===
                                    option.optionText
                                  }
                                  onChange={(e) =>
                                    handleResponseChange(
                                      question.questionId,
                                      e.target.value,
                                    )
                                  }
                                  style={{
                                    marginRight: "16px",
                                    accentColor: PRIMARY_COLOR,
                                    width: "20px",
                                    height: "20px",
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {option.optionText}
                                  {option.enableSuccessor && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{
                                        ml: 1,
                                        color: PRIMARY_COLOR,
                                        fontStyle: "italic",
                                        fontSize: "0.8rem",
                                      }}
                                    />
                                  )}
                                </Typography>
                              </Box>
                              
                              {/* Original Option Text Allow Typing Input */}
                              {option.allowtyping &&
                                responses[question.questionId] ===
                                  option.optionText && (
                                  <Box sx={{ ml: 5, mr: 2, mt: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type={option.isNbr ? "number" : "text"}
                                      placeholder="Please specify..."
                                      value={
                                        otherResponses[question.questionId] || ""
                                      }
                                      onChange={(e) => {
                                        let val = e.target.value;
                                        if (option.isNbr) {
                                          val = val.replace(/[^0-9.]/g, "");
                                        }
                                        handleOtherResponseChange(
                                          question.questionId,
                                          val,
                                        );
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                        },
                                      }}
                                      autoFocus
                                    />
                                  </Box>
                                )}
                                
                              {/* Nested Question Inline Input */}
                              {option.hasAnotherQuestion &&
                                responses[question.questionId] ===
                                  option.optionText && (
                                  <Box sx={{ ml: 5, mr: 2, mt: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: PRIMARY_COLOR,
                                      }}
                                    >
                                      {option.questionTexts}
                                      <span style={{ color: "#d93025", marginLeft: "4px" }}>*</span>
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type={isRelatedPickDate ? "date" : "text"}
                                      placeholder={isRelatedPickDate ? "" : "Your answer..."}
                                      InputLabelProps={isRelatedPickDate ? { shrink: true } : undefined}
                                      value={responses[option.relatedQuestion] || ""}
                                      onChange={(e) => {
                                        handleResponseChange(
                                          option.relatedQuestion,
                                          e.target.value
                                        );
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                        },
                                      }}
                                      autoFocus={!isRelatedPickDate}
                                    />
                                  </Box>
                                )}
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                ))}

                {isLastPage && (
                  <Box
                    sx={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      marginBottom: "24px",
                      padding: "24px",
                      border: "2px solid #e9ecef",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: "16px",
                        color: PRIMARY_COLOR,
                        fontSize: "1.1rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      💬 Please share any improvements or suggestions that would
                      make this system more useful for you.*
                    </Typography>

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Your feedback is valuable to us. Please share any thoughts, suggestions, or recommendations that could help us improve our services..."
                      variant="outlined"
                      required
                      error={!feedback.trim() && feedback !== ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: PRIMARY_COLOR,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: PRIMARY_COLOR,
                          },
                        },
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        color: !feedback.trim() ? "#ef4444" : "#6b7280",
                        mt: 1,
                        display: "block",
                      }}
                    >
                      {!feedback.trim()
                        ? "* This field is required to submit the survey."
                        : "Thank you for providing your feedback!"}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <Button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  sx={{
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor:
                      currentPage === 1 ? "#f0f0f0" : PRIMARY_COLOR,
                    color: currentPage === 1 ? "#666" : "white",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    fontSize: "1rem",
                    "&:hover": {
                      backgroundColor:
                        currentPage === 1 ? "#f0f0f0" : SECONDARY_COLOR,
                    },
                  }}
                >
                  Previous
                </Button>

                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  Page {currentPage} of {totalPages}
                </Typography>

                {currentPage === totalPages ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !feedback.trim()}
                    sx={{
                      padding: "10px 24px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor:
                        submitting || !feedback.trim() ? "#cccccc" : "#16a34a",
                      color: "white",
                      cursor:
                        submitting || !feedback.trim()
                          ? "not-allowed"
                          : "pointer",
                      transition: "all 0.2s ease",
                      fontSize: "1rem",
                      "&:hover": {
                        backgroundColor:
                          submitting || !feedback.trim()
                            ? "#cccccc"
                            : "#15803d",
                      },
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Submit Survey"
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNextPage}
                    sx={{
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor: PRIMARY_COLOR,
                      color: "white",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontSize: "1rem",
                      "&:hover": {
                        backgroundColor: SECONDARY_COLOR,
                      },
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default SurveyFormView;